import type { Conversation } from '@botpress/client'
import { Button, Card, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@bpinternal/ui-kit-next'
import { useQuery } from '@tanstack/react-query'
import type { FC } from 'react'
import { cn } from '~/utils'
import { getIntegrationByNameQueryOptions, getStateQueryOptions } from '../../../queries'
import { EmptyState, Icon } from '../../../elementsv2'
import { DateTime } from 'luxon'
import { Identifier } from '../../../componentsV2'
import { Link, useNavigate } from '@tanstack/react-router'
import EmptyStateIcon from '~/assets/programming-04.svg?react'

type Props = { className?: string; conversations: Conversation[]; workspaceId: string; botId: string }

export const ConversationsTable = ({ className, conversations, workspaceId, botId, ...props }: Props) => {
  return (
    <Card {...props} className={cn(className, 'p-0')}>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead></TableHead>
            <TableHead className="whitespace-nowrap pr-8">Integration</TableHead>
            <TableHead className="whitespace-nowrap pr-8">Last Modified</TableHead>
            <TableHead className="whitespace-nowrap pr-8">Conversation Identifier</TableHead>
            <TableHead className="w-full">Summary</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {conversations.length === 0 ? (
            <TableCell colSpan={5} className="text-center">
              <EmptyState
                iconSize={6}
                icon={EmptyStateIcon}
                className="col-[1/-1] py-16"
                title="You don't have any conversations!"
                description="Conversations will appear here once you start interacting with your bot."
                primaryAction={
                  <Link to="/workspaces/$workspaceId/bots/$botId/webchat" params={{ workspaceId, botId }}>
                    <Button>Start chatting!</Button>
                  </Link>
                }
              />
            </TableCell>
          ) : (
            conversations.map((conversation) => (
              <ConversationRow key={conversation.id} workspaceId={workspaceId} botId={botId} {...conversation} />
            ))
          )}
        </TableBody>
      </Table>
    </Card>
  )
}

type ConversationRowProps = Conversation & {
  workspaceId: string
  botId: string
}
const ConversationRow: FC<ConversationRowProps> = ({ botId, workspaceId, id, integration, updatedAt }) => {
  const navigate = useNavigate()
  const { data: fetchedIntegration } = useQuery(getIntegrationByNameQueryOptions({ name: integration, workspaceId }))
  const { data: fetchedState } = useQuery(
    getStateQueryOptions({
      botId,
      workspaceId,
      id,
      type: 'conversation',
      name: 'agentsConversationVariables',
    })
  )

  return (
    <TableRow
      onClick={() =>
        navigate({
          to: '/workspaces/$workspaceId/bots/$botId/conversations/$conversationId',
          params: { workspaceId, botId, conversationId: id },
        })
      }
      className="cursor-pointer"
    >
      <TableCell>
        <Icon
          size="3"
          className="-mr-2"
          variant={'surface'}
          color={'gray'}
          icon={(props) => <img src={fetchedIntegration?.iconUrl} {...props} />}
        />
      </TableCell>
      <TableCell className="whitespace-nowrap pr-8 font-medium text-gray-12">{fetchedIntegration?.title}</TableCell>
      <TableCell className="whitespace-nowrap pr-8 font-light text-gray-12">
        {DateTime.fromISO(updatedAt).toRelative()}
      </TableCell>
      <TableCell className="whitespace-nowrap pr-8">
        <Identifier id={id} logsStartDate={DateTime.fromISO(updatedAt).minus({ days: 1 }).toISODate()} />
      </TableCell>
      <TableCell className="line-clamp-3 w-full">{fetchedState?.state?.payload.SummaryAgent?.summary}</TableCell>
    </TableRow>
  )
}
